'use client';

import { ArrowRight, Clock, Mail, MapPin, Phone } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { UnifiedContainer } from '@/components/layout/UnifiedContainer';
import { Button } from '@/components/ui/button';
import { CookieSettingsButton } from '@/components/ui/cookie-banner';
import { PhoneCallLink } from '@/components/ui/phone-call-link';
import { ViberLink } from '@/components/ui/viber-link';
import { WhatsAppLink } from '@/components/ui/whatsapp-link';
import { Link } from '@/i18n/routing';
import { PHONE_DISPLAY, SITE_EMAIL } from '@/lib/contact';
import {
  listFooterNeighborhoodSlugs,
  sofiaServiceAreaPath,
} from '@/lib/seo/sofia-service-areas';

export function Footer() {
  const tNav = useTranslations('common.nav');
  const tServices = useTranslations('home.services');
  const tServiceLabels = useTranslations('common.footer.serviceLabels');
  const tButtons = useTranslations('common.buttons');
  const tFooter = useTranslations('common.footer');
  const tAreas = useTranslations('serviceAreas');
  const tReveal = useTranslations('common.phoneReveal');
  const featuredNeighborhoods = listFooterNeighborhoodSlugs();
  const providerTermsLabel = tFooter.has('providerTerms')
    ? tFooter('providerTerms')
    : tFooter.has('providerHelp')
      ? tFooter('providerHelp')
      : 'Provider Terms';

  return (
    <footer className='bg-oxford-blue text-white [&_a]:rounded-sm [&_a:focus-visible]:outline-none [&_a:focus-visible]:ring-2 [&_a:focus-visible]:ring-orange-peel [&_a:focus-visible]:ring-offset-2 [&_a:focus-visible]:ring-offset-oxford-blue'>
      <UnifiedContainer size='2xl' padding='lg'>
        {/* ── Main grid ── */}
        <div className='grid grid-cols-2 gap-x-6 gap-y-8 pt-10 pb-8 sm:gap-x-8 md:gap-x-10 md:pt-14 md:pb-10 lg:grid-cols-5 lg:gap-x-12'>
          {/* Brand */}
          <div className='col-span-2 lg:col-span-1'>
            <Link
              href='/'
              aria-label='Kleaners - Go to homepage'
              className='mb-4 flex w-fit items-center gap-2 hover:opacity-90 transition-opacity'
            >
              {/* Native img: Next/Image + unoptimized SVG can SSR/hydrate different markup under Turbopack */}
              <img
                src='/images/Logos/logo-1.svg'
                alt='Kleaners Logo'
                width={48}
                height={48}
                className='h-10 w-10 flex-shrink-0 brightness-0 invert sm:h-11 sm:w-11'
                style={{ objectFit: 'contain', backgroundColor: 'transparent' }}
              />
              <span className='text-lg sm:text-xl font-semibold leading-none tracking-wide text-white'>
                Kleaners
              </span>
            </Link>
            <p className='max-w-xs text-sm leading-relaxed text-white/80'>{tFooter('tagline')}</p>

            {/* Messaging */}
            <div className='mt-5 flex items-center gap-3'>
              <WhatsAppLink
                ariaLabel={tReveal('whatsAppLabel')}
                className='flex h-9 w-9 items-center justify-center rounded-lg bg-white/[0.08] transition-colors hover:bg-white/15'
              >
                <img
                  src='/images/Logos/whatsapp.svg'
                  alt=''
                  width={20}
                  height={20}
                  className='h-5 w-5'
                />
              </WhatsAppLink>
              <ViberLink
                ariaLabel={tReveal('viberLabel')}
                className='flex h-9 w-9 items-center justify-center rounded-lg bg-white/[0.08] transition-colors hover:bg-white/15'
              >
                <img
                  src='/images/Logos/viber.svg'
                  alt=''
                  width={20}
                  height={20}
                  className='h-5 w-5'
                />
              </ViberLink>
              <a
                href={`mailto:${SITE_EMAIL}`}
                aria-label='E-Mail'
                className='flex h-9 w-9 items-center justify-center rounded-lg bg-white/[0.08] transition-colors hover:bg-white/15'
              >
                <Mail className='h-4 w-4 text-white/80' />
              </a>
            </div>

            {/* Payments */}
            <div className='mt-5 flex flex-col gap-2'>
              <span className='text-xs text-white/80'>{tFooter('acceptedPayments')}</span>
              <div className='flex items-center gap-3 opacity-50'>
                {['Visa', 'MasterCard', 'AmericanExpress', 'Stripe'].map(name => (
                  <img
                    key={name}
                    src={`/images/Logos/${name}.svg`}
                    alt={name}
                    width={48}
                    height={30}
                    className='h-[30px] w-auto brightness-0 invert'
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className='mb-3 text-xs font-bold uppercase tracking-widest text-orange-peel-500 md:mb-4'>
              {tNav('services')}
            </h3>
            <ul className='space-y-2'>
              <li>
                <Link
                  href='/services/office-cleaning'
                  className='text-sm text-white/80 transition-colors hover:text-white'
                >
                  {tServiceLabels('officeCleaning')}
                </Link>
              </li>
              {(['residential', 'commercial', 'outdoor', 'specialized'] as const).map(key => (
                <li key={key}>
                  <Link
                    href={`/services/${key}`}
                    className='text-sm text-white/80 transition-colors hover:text-white'
                  >
                    {tServices(key)}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  href='/services/window-cleaning'
                  className='text-sm text-white/80 transition-colors hover:text-white'
                >
                  {tServiceLabels('windowCleaning')}
                </Link>
              </li>
              <li>
                <Link
                  href='/services'
                  className='mt-1 inline-flex items-center gap-1 text-sm font-medium text-orange-peel-500 transition-colors hover:text-orange-peel-600'
                >
                  {tButtons('viewAll')}
                  <ArrowRight className='h-3 w-3' />
                </Link>
              </li>
            </ul>
          </div>

          {/* Neighborhoods */}
          <div>
            <h3 className='mb-3 text-xs font-bold uppercase tracking-widest text-orange-peel-500 md:mb-4'>
              {tFooter('serviceAreas')}
            </h3>
            <ul className='space-y-2'>
              {featuredNeighborhoods.map(slug => (
                <li key={slug}>
                  <Link
                    href={sofiaServiceAreaPath(slug)}
                    className='text-sm text-white/80 transition-colors hover:text-white'
                  >
                    {tAreas(`areas.${slug}.name`)}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  href='/service-areas'
                  className='mt-1 inline-flex items-center gap-1 text-sm font-medium text-orange-peel-500 transition-colors hover:text-orange-peel-600'
                >
                  {tButtons('viewAll')}
                  <ArrowRight className='h-3 w-3' />
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className='mb-3 text-xs font-bold uppercase tracking-widest text-orange-peel-500 md:mb-4'>
              {tFooter('information')}
            </h3>
            <ul className='space-y-2'>
              {[
                { href: '/pricing', label: tNav('pricing') },
                { href: '/how-it-works', label: tFooter('howItWorks') },
                { href: '/cleaning-options', label: tFooter('cleaningOptions') },
                { href: '/about', label: tNav('about') },
                { href: '/faq', label: tFooter('faq') },
                { href: '/blog', label: tFooter('blog') },
                { href: '/reviews', label: tFooter('reviews') },
              ].map(link => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className='text-sm text-white/80 transition-colors hover:text-white'
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className='mb-3 text-xs font-bold uppercase tracking-widest text-orange-peel-500 md:mb-4'>
              {tFooter('reachUs')}
            </h3>
            <ul className='space-y-3 text-sm'>
              <li className='flex items-start gap-2'>
                <MapPin className='mt-0.5 h-4 w-4 shrink-0 text-white/50' />
                <address
                  className='not-italic leading-snug text-white/80'
                  itemProp='address'
                  itemScope
                  itemType='https://schema.org/PostalAddress'
                >
                  <span itemProp='addressLocality'>Sofia</span>,{' '}
                  <span itemProp='streetAddress'>ul. &quot;Atanas Hranov&quot; 76Б</span>
                </address>
              </li>
              <li className='flex items-center gap-2'>
                <Mail className='h-4 w-4 shrink-0 text-white/50' />
                <a
                  href={`mailto:${SITE_EMAIL}`}
                  className='text-white/80 transition-colors hover:text-white'
                >
                  {SITE_EMAIL}
                </a>
              </li>
              <li className='flex items-center gap-2'>
                <Phone className='h-4 w-4 shrink-0 text-white/50' />
                <PhoneCallLink
                  className='text-white/80 transition-colors hover:text-white'
                  ariaLabel={PHONE_DISPLAY}
                >
                  {PHONE_DISPLAY}
                </PhoneCallLink>
              </li>
              <li className='flex items-center gap-2'>
                <Clock className='h-4 w-4 shrink-0 text-white/50' />
                <span className='text-white/80'>{tFooter('phoneHours')}</span>
              </li>
            </ul>

            {/* Provider CTA */}
            <div className='mt-5'>
              <Button
                asChild
                size='sm'
                className='h-9 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary-hover'
              >
                <Link href='/providers'>
                  <img
                    src='/images/SVG/eagle.svg'
                    alt=''
                    width={16}
                    height={16}
                    className='mr-1.5 h-4 w-4'
                  />
                  {tFooter('becomeProvider')}
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className='border-t border-white/10 py-5 md:py-6'>
          {/* Legal links + copyright */}
          <div className='flex flex-col items-center gap-3 text-center md:flex-row md:justify-between md:text-left'>
            <p className='text-xs text-white/80'>
              {tFooter('copyright', { year: new Date().getFullYear() })}
            </p>
            <nav aria-label='Legal' className='flex flex-wrap justify-center gap-x-4 gap-y-1'>
              {[
                { href: '/legal/terms', label: tFooter('termsShort') },
                { href: '/legal/business-terms', label: tFooter('businessTerms') },
                { href: '/legal/privacy', label: tFooter('privacyShort') },
                { href: '/legal/impressum', label: tFooter('imprint') },
                { href: '/legal/cancellation', label: tFooter('cancellationRights') },
                { href: '/legal/cookie-policy', label: tFooter('cookiePolicy') },
                { href: '/legal/provider-terms', label: providerTermsLabel },
                { href: '/insurance-safety', label: tFooter('insuranceSafety') },
              ].map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className='text-xs text-white/80 transition-colors hover:text-white'
                >
                  {link.label}
                </Link>
              ))}
              <CookieSettingsButton className='text-xs text-white/80 transition-colors hover:text-white' />
            </nav>
          </div>
        </div>
      </UnifiedContainer>
    </footer>
  );
}
